import { getBootstrapData } from "@swa-ui/bootstrap";

import { getPersistedCarBookingData, getPersistedCarProductData } from "../carPersistence";
import { ORDER_ID_CONTEXT } from "../defines";

/**
 * @typedef {Object} CarBookingDetails
 * @property {String} confirmationNumber Reservation confirmation number used as the ORDERID requestor ID.
 * @property {String} email Driver's email address.
 * @property {String} firstName Driver's first name.
 * @property {String} flightNumber Flight number for the arrival details.
 * @property {String} internalReferenceNumber Corporate internal reference number.
 * @property {String} membershipID Rapid Rewards membership ID.
 * @property {String} supplierLoyaltyID Customer's supplier loyalty program membership ID.
 * @property {String} supplierName Name of the supplier (e.g., Avis, Hertz).
 * @property {String} surname Driver's last name.
 * @property {String} telephone Driver's phone number.
 */

/**
 * @typedef {Object} CreditCardDetails
 * @property {String} cardType Credit card type (e.g., VISA, MASTERCARD, AMEX).
 * @property {String} cityTown Billing address city or town.
 * @property {String} country Billing address country code.
 * @property {String} encryptedCardNumber Encrypted credit card number.
 * @property {String} expirationDate Card expiration date in MM/YY format or MM/YYYY format.
 * @property {String} firstNameOnCard First name as it appears on the card.
 * @property {String} lastNameOnCard Last name as it appears on the card.
 * @property {String} savedCreditCardId Token for the saved credit card. When provided, PaymentCard
 *   is left untouched and saved_credit_card_id is returned as a top-level payload field instead.
 * @property {String} securityCode Card security code (CVV).
 * @property {String} state Billing address state.
 * @property {String} streetAddress Billing address street.
 * @property {String} streetAddressSecond Billing address second line.
 * @property {String} zipCode Billing address zip code.
 */

/**
 * Updates the driver's personal and contact information on the Primary customer object
 *
 * @param {Object} primary The Primary customer object from the booking payload
 * @param {CarBookingDetails} carBookingDetails Booking details for the car reservation
 */
const updateDriverDetails = (primary, carBookingDetails) => {
  const { email, firstName, surname, telephone } = carBookingDetails;

  if (firstName || surname) {
    primary.PersonName = {
      ...primary.PersonName,
      ...(firstName && { GivenName: firstName }),
      ...(surname && { Surname: surname }),
    };
  }
  if (telephone) {
    primary.Telephone = [{ ...primary.Telephone?.[0], "@PhoneNumber": telephone }];
  }

  if (email) {
    primary.Email = { ...primary.Email, "#text": email };
  }
};

/**
 * Updates the Rapid Rewards membership ID on the CustLoyalty object
 *
 * @param {Object} primary The Primary customer object from the booking payload
 * @param {String} membershipID The Rapid Rewards membership ID
 */
const updateRapidRewardsNumber = (primary, membershipID) => {
  if (membershipID) {
    primary.CustLoyalty = { ...primary.CustLoyalty, "@MembershipID": membershipID };
  }
};

/**
 * Adds the supplier loyalty benefit to the Primary customer's TPA_Extensions
 *
 * @param {Object} primary The Primary customer object from the booking payload
 * @param {CarBookingDetails} carBookingDetails Booking details for the car reservation
 */
const updateSupplierLoyalty = (primary, carBookingDetails) => {
  const { supplierLoyaltyID, supplierName } = carBookingDetails;
  const { supplierCodes = {} } = getBootstrapData("car-vendor-config") || {};
  const supplierCode = supplierCodes[supplierName?.toUpperCase()];

  if (supplierLoyaltyID && supplierCode) {
    primary.TPA_Extensions = {
      ...primary.TPA_Extensions,
      Benefits: {
        Benefit: {
          "@xmlType": supplierCode,
          "@type": "MEMBERSHIP_NUMBER",
          "@code": supplierLoyaltyID,
        },
      },
    };
  }
};

/**
 * Updates the flight number on the ArrivalDetails in VehResRQInfo
 *
 * @param {Object} payload The booking payload
 * @param {String} flightNumber The flight number
 */
const updateFlightNumber = (payload, flightNumber) => {
  if (flightNumber) {
    payload.VehResRQInfo = {
      ...payload.VehResRQInfo,
      ArrivalDetails: {
        ...payload.VehResRQInfo?.ArrivalDetails,
        "@TransportationCode": "14",
        "@Number": flightNumber,
      },
    };
  }
};

/**
 * Adds the corporate internal reference number to the GenericData in VehResRQInfo
 *
 * @param {Object} payload The booking payload
 * @param {String} internalReferenceNumber The corporate internal reference number
 */
const updateInternalReferenceNumber = (payload, internalReferenceNumber) => {
  if (internalReferenceNumber) {
    const existingData = payload?.VehResRQInfo?.TPA_Extensions?.GenericData?.Data || [];

    payload.VehResRQInfo = {
      ...payload.VehResRQInfo,
      TPA_Extensions: {
        ...payload.VehResRQInfo?.TPA_Extensions,
        GenericData: {
          ...payload.VehResRQInfo?.TPA_Extensions?.GenericData,
          Data: [
            ...existingData,
            { "@name": "Internal_reference_number", "#text": internalReferenceNumber },
          ],
        },
      },
    };
  }
};

/**
 * Sets the confirmation number as the requestor ID on the ORDERID source in POS
 *
 * @param {Object} payload The booking payload
 * @param {String} confirmationNumber The reservation confirmation number
 */
const updateRequestorId = (payload, confirmationNumber) => {
  const orderIdSource = payload.POS?.Source?.find(
    (source) => source.RequestorID?.["@ID_Context"] === ORDER_ID_CONTEXT
  );

  if (orderIdSource) {
    orderIdSource.RequestorID["@ID"] = confirmationNumber;
  }
};

/**
 * Populates RentalPaymentPref.PaymentCard and Primary.Address with credit card and billing data.
 * Only applies when the persisted payload already contains a RentalPaymentPref slot (indicating
 * the vendor requires a credit card).
 *
 * @param {Object} payload The booking payload
 * @param {Object} primary The Primary customer object from the booking payload
 * @param {CreditCardDetails} creditCardDetails Credit card and billing details
 */
const updateCreditCardData = (payload, primary, creditCardDetails) => {
  if (payload?.VehResRQInfo?.RentalPaymentPref) {
    const {
      cardType,
      cityTown,
      country,
      encryptedCardNumber,
      expirationDate,
      firstNameOnCard,
      lastNameOnCard,
      securityCode,
      state,
      streetAddress,
      streetAddressSecond,
      zipCode,
    } = creditCardDetails;

    const { paymentCodes = {} } = getBootstrapData("car-vendor-config") || {};
    const addressLine = [streetAddress, streetAddressSecond].filter(Boolean).join(" ");
    const [expireMonth, expireYear] = expirationDate?.split("/") ?? [];
    const formattedExpireDate = `${expireMonth}${expireYear.slice(-2)}`;

    payload.VehResRQInfo.RentalPaymentPref.PaymentCard = {
      // TODO: Confirm with CarTrawler the required field ordering and whether ThreeDomainSecurity is required. (SPASHOP-1305)
      "@CardCode": paymentCodes[cardType?.toUpperCase()],
      "@ExpireDate": formattedExpireDate,
      CardHolderName: `${firstNameOnCard} ${lastNameOnCard}`.trim(),
      CardNumber: {
        PlainText: encryptedCardNumber,
      },
      SeriesCode: {
        PlainText: securityCode,
      },
      ThreeDomainSecurity: {
        Gateway: {
          "@ECI": "X",
        },
      },
    };

    /* eslint-disable sort-keys */
    primary.Address = {
      "@Type": "2",
      AddressLine: addressLine,
      CityName: cityTown ?? "",
      PostalCode: zipCode ?? "",
      StateProv: state ?? "",
      CountryName: { "@Code": country ?? "" },
    };
  }
};

/**
 * This function retrieves the persisted CarTrawler booking data and generates a payload
 * sent to the car/bookings api
 *
 * @param {CarBookingDetails} carBookingDetails Booking details for the car reservation
 * @param {CreditCardDetails} [creditCardDetails] Optional credit card and billing details
 * @returns {Object} Object containing ct_content and ct_full_url
 */
export const generateBookingPayload = (carBookingDetails, creditCardDetails) => {
  const { insuranceSelected } = getPersistedCarProductData();
  const hasInsurance = creditCardDetails && insuranceSelected;
  const hasSavedCard = hasInsurance && !!creditCardDetails.savedCreditCardId;
  const { msgRaw, fullUrl } = getPersistedCarBookingData() || {};
  const payload = msgRaw || {};
  const primary = payload?.VehResRQCore?.Customer?.Primary || {};

  updateDriverDetails(primary, carBookingDetails);
  updateRapidRewardsNumber(primary, carBookingDetails.membershipID);
  updateSupplierLoyalty(primary, carBookingDetails);
  updateInternalReferenceNumber(payload, carBookingDetails.internalReferenceNumber);
  updateFlightNumber(payload, carBookingDetails.flightNumber);

  if (carBookingDetails?.confirmationNumber) {
    updateRequestorId(payload, carBookingDetails.confirmationNumber);
  }

  if (hasInsurance && !hasSavedCard) {
    updateCreditCardData(payload, primary, creditCardDetails);
  }

  return {
    ct_content: JSON.stringify(payload),
    ct_full_url: fullUrl,
    ...(hasSavedCard && { saved_credit_card_id: creditCardDetails.savedCreditCardId }),
  };
};
